import React, { useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../../lib/api";
import { formatApiErrorDetail } from "../../../lib/constants";

export default function AdminSettings({ onUsernameChange }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newUsername, setNewUsername] = useState(localStorage.getItem("ayome_admin_username") || "");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const inp = "w-full h-11 px-3 rounded-md bg-gray-100 focus:bg-white border border-gray-200 focus:border-[#228B22] outline-none transition-colors text-sm";

  const submit = async (e) => {
    e.preventDefault();
    if (!currentPassword) { toast.error("Enter your current password"); return; }
    if (newPassword && newPassword.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (newPassword && newPassword !== confirm) { toast.error("New passwords do not match"); return; }
    const payload = { current_password: currentPassword };
    const trimmedUser = newUsername.trim();
    if (trimmedUser) payload.new_username = trimmedUser;
    if (newPassword) payload.new_password = newPassword;
    if (!payload.new_username && !payload.new_password) { toast.error("Nothing to update"); return; }
    setSaving(true);
    try {
      const { data } = await adminApi.put("/admin/credentials", payload);
      localStorage.setItem("ayome_admin_username", data.username);
      if (onUsernameChange) onUsernameChange(data.username);
      toast.success("Credentials updated successfully");
      setCurrentPassword(""); setNewPassword(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-md">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={18} className="text-[#228B22]" />
          <h3 className="font-bold text-[#0A1F13]">Admin Credentials</h3>
        </div>
        <p className="text-sm text-gray-500 mb-5">Update your admin username and password. Enter your current password to confirm.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm text-gray-500 mb-1 block">Current Password</label>
            <input data-testid="current-password-input" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className={inp} autoComplete="current-password" />
          </div>
          <div className="border-t border-gray-100 pt-4 space-y-4">
            <div>
              <label className="text-sm text-gray-500 mb-1 block">New Username</label>
              <input data-testid="new-username-input" value={newUsername} onChange={(e) => setNewUsername(e.target.value)} className={inp} autoComplete="username" />
            </div>
            <div>
              <label className="text-sm text-gray-500 mb-1 block">New Password <span className="text-gray-400">(leave blank to keep current)</span></label>
              <input data-testid="new-password-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={inp} autoComplete="new-password" />
            </div>
            <div>
              <label className="text-sm text-gray-500 mb-1 block">Confirm New Password</label>
              <input data-testid="confirm-password-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inp} autoComplete="new-password" />
            </div>
          </div>
          <button data-testid="save-credentials-button" type="submit" disabled={saving} className="w-full h-11 rounded-md bg-[#228B22] hover:bg-[#1A4331] text-white font-semibold flex items-center justify-center transition-colors disabled:opacity-60">
            {saving ? <Loader2 size={18} className="animate-spin" /> : "Update Credentials"}
          </button>
        </form>
      </div>
    </div>
  );
}
